import { prepareImageUpload } from './api.js';

// Shared helpers for resident media uploads. Used by the resident edit
// sheet on Home and the custom-species form in the catalog browser so
// both routes accept the same mix of photos + short videos.

export const IMAGE_MIMES = /^image\//i;
export const VIDEO_MIMES = /^video\//i;
export const VIDEO_MAX_BYTES = 25 * 1024 * 1024;
export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm';
export const MEDIA_ACCEPT = `image/*,.heic,.heif,${VIDEO_ACCEPT}`;

export function isHeic(file) {
  return /image\/(heic|heif)/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
}

export function looksLikeImage(file) {
  return IMAGE_MIMES.test(file.type || '') || isHeic(file);
}

export function looksLikeVideo(file) {
  return VIDEO_MIMES.test(file.type || '');
}

// Pull a single JPEG frame from a user-supplied video so the server
// doesn't have to crack the container to render a thumbnail. Returns
// { base64, durationS } on success or null on any decode failure
// (browser-specific codec gaps, DRM, etc.).
export function grabPosterFrame(file) {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;
    const url = URL.createObjectURL(file);
    let done = false;
    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* already revoked */
      }
    };
    v.onloadedmetadata = () => {
      try {
        v.currentTime = Math.min(0.5, (v.duration || 1) / 4);
      } catch {
        if (!done) {
          done = true;
          cleanup();
          resolve(null);
        }
      }
    };
    v.onseeked = () => {
      if (done) return;
      done = true;
      try {
        const w = Math.min(480, v.videoWidth || 480);
        const h = w * ((v.videoHeight || 270) / (v.videoWidth || 480));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(v, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            cleanup();
            if (!blob) return resolve(null);
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = String(reader.result || '');
              resolve({ base64: dataUrl.split(',')[1] || null, durationS: v.duration || null });
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          0.78
        );
      } catch {
        cleanup();
        resolve(null);
      }
    };
    v.onerror = () => {
      if (!done) {
        done = true;
        cleanup();
        resolve(null);
      }
    };
    v.src = url;
  });
}

// Upload a single media file (image or video) to a freshly-created
// resident. Centralised so the resident edit sheet and the
// custom-species form share the same HEIC handling, poster-frame
// extraction, and share-with-catalog behaviour.
export async function uploadResidentMedia({ tankId, residentId, file, shareWithCatalog }) {
  if (looksLikeImage(file)) {
    // Canvas-first: Safari decodes HEIC natively so iPhones upload a
    // small JPEG instead of multi-MB raw HEIC (server heic-convert is
    // memory-heavy enough to 502 a small container). Raw only when
    // this browser can't decode.
    const { body, mime } = await prepareImageUpload(file, { maxDim: 1600, quality: 0.85 });
    const qs = shareWithCatalog ? '?share=1' : '';
    const r = await fetch(`/api/tanks/${tankId}/livestock/${residentId}/image${qs}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': mime },
      body,
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error || `Upload failed (${r.status}).`);
    }
    return { kind: 'image' };
  }
  if (looksLikeVideo(file)) {
    if (file.size > VIDEO_MAX_BYTES) {
      throw new Error('Video too large (25 MB max).');
    }
    let posterB64 = null;
    let durationS = null;
    try {
      const meta = await grabPosterFrame(file);
      if (meta) {
        posterB64 = meta.base64;
        durationS = meta.durationS;
      }
    } catch {
      /* poster optional */
    }
    const qs = new URLSearchParams();
    if (shareWithCatalog) qs.set('share', '1');
    if (durationS) qs.set('duration', String(Math.round(durationS * 10) / 10));
    const headers = { 'Content-Type': file.type || 'video/mp4' };
    if (posterB64) headers['X-Poster-Base64'] = posterB64;
    const r = await fetch(`/api/tanks/${tankId}/livestock/${residentId}/video?${qs.toString()}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: file,
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error || `Upload failed (${r.status}).`);
    }
    return { kind: 'video' };
  }
  throw new Error("That doesn't look like a photo or video.");
}
